import { gql } from '@apollo/client';
import { Box, Table, Tbody, Td, Th, Thead, Tooltip, Tr } from '@chakra-ui/react';
import { useEffect } from 'react';
import { TableInstance } from 'react-table';
import { ChevronDownIcon, ChevronUpIcon } from '~components/ui/icons';
import { FIRST_COLUMN_SHADOW, LAST_COLUMN_SHADOW, useTableScroll } from '../useTableScroll';
import { UsersTableUserFragment } from './__generated__/UsersTable.graphql';
import { UserTableFilter } from './Filtering';
import { UsersTablePageSizeSelector, UsersTablePagination } from './Pagination';

interface Props {
  table: TableInstance<UsersTableUserFragment>;
}

export function UsersTable({ table }: Props) {
  const { getTableProps, getTableBodyProps, headerGroups, prepareRow, page } = table;
  const { tableRef, canScrollLeft, canScrollRight, checkScroll } = useTableScroll();

  useEffect(() => {
    checkScroll();
  }, [page, checkScroll]);

  return (
    <Table
      ref={tableRef}
      {...getTableProps()}
      variant="simple"
      minWidth={{ base: '700px', lg: 'calc(100% + 150px)' }}
      sx={{
        borderCollapse: 'separate',
        borderSpacing: 0,
      }}
    >
      <Thead>
        {headerGroups.map((headerGroup) => {
          const headerGroupProps = headerGroup.getHeaderGroupProps();
          const totalHeaders = headerGroup.headers.length;

          return (
            <Tr {...headerGroupProps} key={headerGroupProps.key}>
              {headerGroup.headers.map((column, index) => {
                const sortProps = column.getSortByToggleProps();
                const sortColumn = sortProps.onClick;
                const headerProps = column.getHeaderProps(sortProps);
                const isFirst = index === 0;
                const isLast = index === totalHeaders - 1;

                return (
                  <Th
                    {...headerProps}
                    key={headerProps.key}
                    color={column.isSorted ? 'gray.700' : 'gray.600'}
                    minWidth={column.minWidth ? `${column.minWidth}px` : undefined}
                    maxWidth={column.maxWidth ? `${column.maxWidth}px` : undefined}
                    width={column.width}
                    position={isFirst || isLast ? { base: 'static', lg: 'sticky' } : undefined}
                    left={isFirst ? { base: 'auto', lg: 0 } : undefined}
                    right={isLast ? { base: 'auto', lg: 0 } : undefined}
                    zIndex={isFirst || isLast ? { base: 'auto', lg: 2 } : undefined}
                    bgColor="white"
                    boxShadow={
                      isFirst
                        ? { base: 'none', lg: canScrollLeft ? FIRST_COLUMN_SHADOW : 'none' }
                        : isLast
                        ? { base: 'none', lg: canScrollRight ? LAST_COLUMN_SHADOW : 'none' }
                        : undefined
                    }
                    transition="box-shadow 0.2s ease"
                    _hover={
                      column.disableSortBy
                        ? {}
                        : {
                            textColor: 'gray.800',
                          }
                    }
                    onClick={sortColumn}
                  >
                    <Box display="flex" flexDirection="row" alignItems="center">
                      <Box flex="1">{column.render('Header')}</Box>
                      {column.canSort && (
                        <Box flexShrink={0}>
                          {column.isSorted ? (
                            column.isSortedDesc ? (
                              <ChevronDownIcon display="inherit" />
                            ) : (
                              <ChevronUpIcon display="inherit" />
                            )
                          ) : (
                            <ChevronDownIcon visibility="hidden" />
                          )}
                        </Box>
                      )}
                    </Box>
                  </Th>
                );
              })}
            </Tr>
          );
        })}
      </Thead>
      <Tbody {...getTableBodyProps()}>
        {page.map((row) => {
          prepareRow(row);
          const rowProps = row.getRowProps();
          const totalCells = row.cells.length;

          return (
            <Tr {...rowProps} key={rowProps.key}>
              {row.cells.map((cell, index) => {
                const cellProps = cell.getCellProps();
                const isFirst = index === 0;
                const isLast = index === totalCells - 1;

                return (
                  <Tooltip label={cell.value} hasArrow key={cellProps.key}>
                    <Td
                      {...cellProps}
                      minWidth={cell.column.minWidth ? `${cell.column.minWidth}px` : undefined}
                      maxWidth={cell.column.maxWidth ? `${cell.column.maxWidth}px` : undefined}
                      width={cell.column.width}
                      overflow={!isLast ? 'hidden' : undefined}
                      position={isFirst || isLast ? { base: 'static', lg: 'sticky' } : undefined}
                      left={isFirst ? { base: 'auto', lg: 0 } : undefined}
                      right={isLast ? { base: 'auto', lg: 0 } : undefined}
                      zIndex={isFirst || isLast ? { base: 'auto', lg: 1 } : undefined}
                      bgColor="white"
                      boxShadow={
                        isFirst
                          ? { base: 'none', lg: canScrollLeft ? FIRST_COLUMN_SHADOW : 'none' }
                          : isLast
                          ? { base: 'none', lg: canScrollRight ? LAST_COLUMN_SHADOW : 'none' }
                          : undefined
                      }
                      transition="box-shadow 0.2s ease"
                    >
                      {cell.render('Cell')}
                    </Td>
                  </Tooltip>
                );
              })}
            </Tr>
          );
        })}
      </Tbody>
    </Table>
  );
}

UsersTable.Filter = UserTableFilter;
UsersTable.Pagination = UsersTablePagination;
UsersTable.PageSizeSelector = UsersTablePageSizeSelector;

UsersTable.graphql = {
  fragments: {
    UsersTableUser: gql`
      fragment UsersTableUser on User {
        id
        givenName
        familyName
        email
        roles
        customerIds
      }
    `,
  },
};
